import { useEffect, useId, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

type BrandLoaderProps = {
  variant?: "fullscreen" | "inline";
  label?: string;
  messages?: string[];
  className?: string;
};

const localizedContent = {
  pt: {
    label: "Carregando",
    messages: ["Sincronizando ambiente...", "Otimizando conexões...", "Processando dados...", "Quase pronto..."],
  },
  en: {
    label: "Loading",
    messages: ["Synchronizing workspace...", "Optimizing connections...", "Processing data...", "Almost ready..."],
  },
  es: {
    label: "Cargando",
    messages: ["Sincronizando entorno...", "Optimizando conexiones...", "Procesando datos...", "Casi listo..."],
  },
} as const;

export const BrandLoader = ({
  variant = "inline",
  label,
  messages,
  className,
}: BrandLoaderProps) => {
  const { language } = useLanguage();
  const content = localizedContent[language as keyof typeof localizedContent] ?? localizedContent.pt;
  const loaderLabel = label ?? content.label;
  const loaderMessages = messages ?? content.messages;
  const [messageIndex, setMessageIndex] = useState(0);
  const [isMessageVisible, setIsMessageVisible] = useState(true);
  const messageTimeoutRef = useRef<number | undefined>();
  const gradientId = useId().replace(/:/g, "");
  const isFullscreen = variant === "fullscreen";

  useEffect(() => {
    if (!isFullscreen || loaderMessages.length === 0) return;

    setMessageIndex(0);
    setIsMessageVisible(true);
    const interval = window.setInterval(() => {
      setIsMessageVisible(false);
      messageTimeoutRef.current = window.setTimeout(() => {
        setMessageIndex((current) => (current + 1) % loaderMessages.length);
        setIsMessageVisible(true);
      }, 300);
    }, 2400);

    return () => {
      window.clearInterval(interval);
      if (messageTimeoutRef.current !== undefined) window.clearTimeout(messageTimeoutRef.current);
    };
  }, [isFullscreen, loaderMessages]);

  const size = isFullscreen ? 88 : 56;

  return (
    <div
      className={cn(
        "brand-loader",
        isFullscreen ? "min-h-screen w-full flex items-center justify-center bg-background" : "w-full min-h-[400px] flex items-center justify-center",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="brand-loader-content">
        <div className={cn("brand-loader-mark", isFullscreen ? "w-[88px] h-[88px] mb-8" : "w-14 h-14 mb-5")}>
          <div className="brand-loader-halo" />
          <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" className="brand-loader-svg">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" style={{ stopColor: "var(--brand-loader-color)" }} />
                <stop offset="100%" style={{ stopColor: "color-mix(in srgb, var(--brand-loader-color), #000 12%)" }} />
              </linearGradient>
            </defs>
            <rect width="100" height="100" rx="24" fill={`url(#${gradientId})`} />
            <path className="brand-loader-chevron brand-loader-chevron-top" d="M 43 31 H 68 V 56" />
            <path className="brand-loader-chevron" d="M 25 49 H 50 V 74" />
          </svg>
        </div>
        <div className="brand-loader-title-row">
          <span className="brand-loader-title">{loaderLabel}</span>
          <span className="brand-loader-dots" aria-hidden="true"><i /><i /><i /></span>
        </div>
        {isFullscreen && loaderMessages.length > 0 && (
          <p className={cn("brand-loader-message", !isMessageVisible && "brand-loader-message-exiting")}>
            {loaderMessages[messageIndex]}
          </p>
        )}
        <div className="brand-loader-progress" aria-hidden="true"><span /></div>
      </div>
    </div>
  );
};
